/**
 * Integrytas Group - Base App (MOBILE)
 * Page: DDM.REFRESH — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/DdmRefreshPage.tsx. A DISTINCT
 * presentation (KPI tiles + dense job-card list + bottom-sheet detail) over the
 * SAME data/logic layer:
 *   - reads via apiDb from ddm_refresh_job (+ requester join on
 *     fk_ddm_refresh_job_requested_by → csm_user_master), never raw ids (§17.B4),
 *   - the "Refresh Demo Database" action inserts a ddm_refresh_job row through the
 *     Common Routine (routedInsert) — §2 single write path — exactly like the
 *     desktop page; a pg_cron worker then performs the actual clone/copy.
 * It never re-renders the desktop grid; §16.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Database, RefreshCw, X } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedInsert } from '../../lib/routed_writes';
import { hasAccess, getForensicActorId } from '../../types/auth';
import { useFormatDateTime } from '../../lib/format_date';
import {
  MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow,
  MobileFilterChips, MobileStatusPill, type MobilePillTone, type MobileLeadTone,
} from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

const PAGE_CODE = 'DDM.REFRESH';

type StatusCode = 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';

interface StatusMeta {
  value: StatusCode;
  labelKey: string;
  labelFallback: string;
  pill: MobilePillTone;
  lead: MobileLeadTone;
}

// CHECK-enum labels via labelled constant + t() — never bare stored literals (§17.B11).
const STATUS_META: StatusMeta[] = [
  { value: 'PENDING', labelKey: 'ddm.status.PENDING', labelFallback: 'Pending', pill: 'wait', lead: 'slate' },
  { value: 'RUNNING', labelKey: 'ddm.status.RUNNING', labelFallback: 'Running', pill: 'info', lead: 'blue' },
  { value: 'SUCCEEDED', labelKey: 'ddm.status.SUCCEEDED', labelFallback: 'Succeeded', pill: 'ok', lead: 'green' },
  { value: 'FAILED', labelKey: 'ddm.status.FAILED', labelFallback: 'Failed', pill: 'no', lead: 'red' },
];
const META_BY_CODE: Record<StatusCode, StatusMeta> = STATUS_META.reduce(
  (acc, m) => ({ ...acc, [m.value]: m }),
  {} as Record<StatusCode, StatusMeta>,
);
const ACTIVE_STATUSES = new Set<StatusCode>(['PENDING', 'RUNNING']);
const REFRESH_POLL_MS = 3000;
const JOB_LIMIT = 50;

interface MobileRefreshRow {
  ddm_refresh_job_id: number;
  status_code: StatusCode;
  requested_by: number;
  requester_name: string;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  row_count: number | null;
  error_message: string | null;
}

type StatusFacet = 'all' | StatusCode;

interface RawRequester { display_name: string | null; email: string | null }
interface RawRefreshRow {
  ddm_refresh_job_id: number;
  status_code: StatusCode;
  requested_by: number;
  requester: RawRequester | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  row_count: number | null;
  error_message: string | null;
}

export function MobileDdmRefreshPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [rows, setRows] = useState<MobileRefreshRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('all');
  const [selected, setSelected] = useState<MobileRefreshRow | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const statusLabel = (s: StatusCode) => t(META_BY_CODE[s].labelKey, META_BY_CODE[s].labelFallback);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('ddm_refresh_job')
        .select(
          'ddm_refresh_job_id, status_code, requested_by, requester:csm_user_master!fk_ddm_refresh_job_requested_by(display_name, email), created_at, started_at, completed_at, row_count, error_message',
        )
        .order('created_at', { ascending: false })
        .limit(JOB_LIMIT);
      if (e) throw e;

      const mapped: MobileRefreshRow[] = ((data ?? []) as unknown as RawRefreshRow[]).map((r) => ({
        ddm_refresh_job_id: r.ddm_refresh_job_id,
        status_code: r.status_code,
        requested_by: r.requested_by,
        // §17.B4: resolve the FK to a name at fetch time; never surface the raw id.
        requester_name: r.requester?.display_name || r.requester?.email || t('common.unknown', 'Unknown'),
        created_at: r.created_at,
        started_at: r.started_at,
        completed_at: r.completed_at,
        row_count: r.row_count,
        error_message: r.error_message,
      }));
      setRows(mapped);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, []);

  // Poll while a job is in flight so its status updates without a manual reload,
  // mirroring the desktop page. setInterval never fires synchronously, so this is
  // safe in tests (no re-render loop).
  useEffect(() => {
    const hasActive = rows.some((r) => ACTIVE_STATUSES.has(r.status_code));
    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(() => { void reload(); }, REFRESH_POLL_MS);
    } else if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet !== 'all' && r.status_code !== statusFacet) return false;
      if (!q) return true;
      return r.requester_name.toLowerCase().includes(q) || statusLabel(r.status_code).toLowerCase().includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filter, statusFacet]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.ddm.all', 'All') },
    ...STATUS_META.map((m) => ({ key: m.value as StatusFacet, label: statusLabel(m.value) })),
  ];

  const counts = useMemo(() => {
    const total = rows.length;
    const succeeded = rows.filter((r) => r.status_code === 'SUCCEEDED').length;
    const failed = rows.filter((r) => r.status_code === 'FAILED').length;
    return { total, succeeded, failed };
  }, [rows]);

  // Most recent successful refresh — the "last-refresh date" the screen leads with.
  const lastRefreshAt = useMemo(() => {
    const done = rows.find((r) => r.status_code === 'SUCCEEDED' && r.completed_at);
    return done?.completed_at ?? null;
  }, [rows]);

  const triggerRefresh = async () => {
    setTriggering(true);
    setError(null);
    // §2: route the insert through the Common Routine (forensic stamp + audit + hooks).
    const actor = getForensicActorId(session);
    const { error: e } = await routedInsert(session, 'ddm_refresh_job', { requested_by: actor });
    setTriggering(false);
    if (e) { setError(e.message); return; }
    setConfirming(false);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.ddm.title', 'Demo Data Refresh')}
        description={t('mobile.ddm.description', 'Refresh jobs that clone production into the demo schema, and their status.')}
      />

      <MobileStatStrip
        stats={[
          { value: counts.total.toLocaleString(), label: t('mobile.ddm.kpi_total', 'Total') },
          { value: counts.succeeded.toLocaleString(), label: t('mobile.ddm.kpi_succeeded', 'Succeeded') },
          { value: counts.failed.toLocaleString(), label: t('mobile.ddm.kpi_failed', 'Failed') },
        ]}
      />

      <div className="m-card">
        <div className="m-fieldcard">
          <div className="m-field">
            <span className="m-k">{t('mobile.ddm.last_refresh', 'Last refresh')}</span>
            <span className="m-v">{lastRefreshAt ? formatDateTime(lastRefreshAt) : '—'}</span>
          </div>
        </div>
        <div className="m-actionbar">
          <button
            type="button"
            className="m-btn m-btn-primary"
            disabled={triggering || busy}
            onClick={canChange ? (() => setConfirming(true)) : undefined}
          >
            <RefreshCw size={16} />
            {t('mobile.ddm.trigger', 'Refresh Demo Database')}
          </button>
        </div>
      </div>

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.ddm.search', 'Search jobs')}
          aria-label={t('mobile.ddm.search', 'Search jobs')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.ddm.none', 'No refresh jobs match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => {
            const meta = META_BY_CODE[r.status_code];
            return (
              <MobileCardRow
                key={r.ddm_refresh_job_id}
                lead={<Database />}
                leadTone={meta.lead}
                title={r.requester_name}
                subtitle={formatDateTime(r.created_at)}
                trailing={<MobileStatusPill tone={meta.pill}>{statusLabel(r.status_code)}</MobileStatusPill>}
                chevron
                onClick={() => setSelected(r)}
              />
            );
          })}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.ddm.detail', 'Refresh job detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Database size={20} /></span>
              <span>
                <div className="m-nm">{selected.requester_name}</div>
                <div className="m-em">{formatDateTime(selected.created_at)}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.ddm.status', 'Status')}
                value={<MobileStatusPill tone={META_BY_CODE[selected.status_code].pill}>{statusLabel(selected.status_code)}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.ddm.requested_at', 'Requested at')} value={formatDateTime(selected.created_at)} />
              <MobileDetailRow label={t('mobile.ddm.started_at', 'Started at')} value={selected.started_at ? formatDateTime(selected.started_at) : '—'} />
              <MobileDetailRow label={t('mobile.ddm.completed_at', 'Completed at')} value={selected.completed_at ? formatDateTime(selected.completed_at) : '—'} />
              <MobileDetailRow label={t('mobile.ddm.row_count', 'Rows copied')} value={selected.row_count == null ? '—' : selected.row_count.toLocaleString()} />
              {selected.error_message && (
                <MobileDetailRow
                  label={t('mobile.ddm.error', 'Error')}
                  value={<span style={{ color: 'var(--color-danger)' }}>{selected.error_message}</span>}
                />
              )}
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Confirm-refresh bottom-sheet */}
      <div className={'m-scrim' + (confirming ? ' m-open' : '')} onClick={() => { if (!triggering) setConfirming(false); }} />
      <div className={'m-sheet' + (confirming ? ' m-open' : '')} aria-label={t('mobile.ddm.confirm_title', 'Refresh Demo Database?')}>
        <div className="m-grabber" />
        {confirming && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><RefreshCw size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.ddm.confirm_title', 'Refresh Demo Database?')}</div>
              </span>
            </div>
            <div className="m-fieldcard">
              <div className="m-field">
                <span className="m-v">
                  {t('mobile.ddm.confirm_body', 'This replaces all data in the demo schema with a fresh copy of production. It does not change or affect production data.')}
                </span>
              </div>
            </div>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={triggering} onClick={() => void triggerRefresh()}>
                <RefreshCw size={16} />
                {triggering ? t('mobile.ddm.triggering', 'Starting…') : t('mobile.ddm.confirm', 'Refresh')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={triggering} onClick={() => setConfirming(false)}>
                <X size={16} />
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
